import { Formik, FormikHelpers } from 'formik'
import { isEmpty } from 'lodash'
import { ChangeEvent, ReactElement, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import InputAddon from '@10xLabs/react-web/components/InputAddon'
import { useApolloClient, useMutation } from '@apollo/client'
import { ClipIcon } from 'components/Icons/ClipIcon'
import { InputCurrency } from 'components/InputCurrency'
import { Button } from 'components/v2/Button'
import EmptyState from 'components/v2/EmptyState'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import Heading from 'components/v2/Heading'
import IconButton from 'components/v2/IconButton'
import { FilePreview, InputFile } from 'components/v2/InputFile'
import { Select } from 'components/v2/Select'
import { SwitchWithLabel } from 'components/v2/Switch'
import { Textarea } from 'components/v2/Textarea'
import { tripNoteSchema } from 'features/Trips/components/TripDrawer/Notes/schema'
import { ADD_TRIP_NOTE } from 'features/Trips/mutations-legacy'
import { INoteFormValues } from 'features/Trips/types'
import { mapNoteFormToInput, mapNoteFormToRealDistanceInput, mapNoteFormToRSVPNoteInput } from 'features/Trips/utils'
import { GTPaymentIncidencePaymentMethodInput } from 'gql/types.generated'
import { getUnprocessableErrors } from 'utils/error'
import { usePushFileToS3 } from 'utils/file'
import { sanitizePrice } from 'utils/math'
import { integerToCents, kilometersToMeters } from 'utils/number'
import { device } from 'utils/style'
import { safeFormatMessage } from 'utils/intl'
import {
  TripNoteCategoryInput,
  TripNoteDriverRsvpStatus,
  TripV2Driver,
  TripV2Note,
  TripV2NoteCategory,
  TripV2NoteChangeType
} from 'gql/generated/graphql'
import { TRIP_NOTE_FRAGMENT } from 'features/Trips/queries'
import { NoteItem, operationTypeOptions } from 'features/Trips/components/TripDrawer/Notes/NoteItem'
import { DriverRSVPNote } from 'features/Trips/components/TripDrawer/Notes/DriverRSVPNote'
import { ADD_TRIP_DRIVER_REAL_DISTANCE_NOTE, ADD_TRIP_DRIVER_RSVP_NOTE } from 'features/Trips/mutations'
import { Label } from 'components/v2/Label'

const StyledNumberInput = styled(InputAddon.Input)`
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  ::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`

const DistanceContainer = styled.div`
  width: calc(100% - 2px);
`
const messages = defineMessages({
  categoryLabel: {
    id: 'trip.drawer.timeline.category.label',
    defaultMessage: 'Category'
  },
  categoryPlaceholder: {
    id: 'trip.drawer.timeline.category.placeholder',
    defaultMessage: 'Select a category'
  },
  paymentIncidentsLabel: {
    id: 'trip.drawer.timeline.paymentIncidents.label',
    defaultMessage: 'Payment incidents'
  },
  operationTypeLabel: {
    id: 'trip.drawer.timeline.operationType.label',
    defaultMessage: 'Operation type'
  },
  operationTypePlaceholder: {
    id: 'trip.drawer.timeline.operationType.placeholder',
    defaultMessage: 'Select a operation type'
  },
  paymentTypeLabel: {
    id: 'trip.drawer.timeline.paymentType.label',
    defaultMessage: 'Payment type'
  },
  paymentTypePlaceholder: {
    id: 'trip.drawer.timeline.paymentType.placeholder',
    defaultMessage: 'Select a payment type'
  },
  amountPlaceholder: {
    id: 'trip.drawer.timeline.amount.placeholder',
    defaultMessage: 'Amount'
  },
  messageLabel: {
    id: 'trip.drawer.timeline.message.label',
    defaultMessage: 'Add Note'
  },
  messagePlaceholder: {
    id: 'trip.drawer.timeline.message.placeholder',
    defaultMessage: 'Add Note...'
  }
})

const categoryOptions = defineMessages({
  [TripNoteCategoryInput.Service]: {
    id: 'trip.drawer.notes.category.service',
    defaultMessage: 'Service'
  },
  [TripNoteCategoryInput.Vehicle]: {
    id: 'trip.drawer.notes.category.vehicle',
    defaultMessage: 'Vehicle'
  },
  [TripNoteCategoryInput.Passengers]: {
    id: 'trip.drawer.notes.category.passengers',
    defaultMessage: 'Passengers'
  },
  [TripNoteCategoryInput.Events]: {
    id: 'trip.drawer.notes.category.events',
    defaultMessage: 'Events'
  },
  [TripNoteCategoryInput.Ambassador]: {
    id: 'trip.drawer.notes.category.ambassador',
    defaultMessage: 'Ambassador'
  },
  [TripNoteCategoryInput.Tolls]: {
    id: 'trip.drawer.notes.category.tolls',
    defaultMessage: 'Tolls'
  },
  [TripNoteCategoryInput.Cash]: {
    id: 'trip.drawer.notes.category.cash',
    defaultMessage: 'Deposits'
  },
  [TripNoteCategoryInput.Odometer]: {
    id: 'trip.drawer.notes.category.odometer',
    defaultMessage: 'Odometer'
  },
  [TripNoteCategoryInput.Driver]: {
    id: 'trip.drawer.notes.category.driver',
    defaultMessage: 'Driver'
  },
  [TripV2NoteCategory.DriverRsvp]: {
    id: 'trip.drawer.notes.category.driverRSVPStatus',
    defaultMessage: 'Pase de asistencia'
  }
})

const NOTE_CATEGORIES = [
  TripNoteCategoryInput.Service,
  TripNoteCategoryInput.Vehicle,
  TripNoteCategoryInput.Passengers,
  TripNoteCategoryInput.Events,
  TripNoteCategoryInput.Ambassador,
  TripNoteCategoryInput.Tolls,
  TripNoteCategoryInput.Cash,
  TripNoteCategoryInput.Odometer,
  TripNoteCategoryInput.Driver,
  TripV2NoteCategory.DriverRsvp
]

const paymentTypeOptions = defineMessages({
  [GTPaymentIncidencePaymentMethodInput.Cash]: {
    id: 'trip.drawer.notes.paymentType.cash',
    defaultMessage: 'Cash'
  },
  [GTPaymentIncidencePaymentMethodInput.Card]: {
    id: 'trip.drawer.notes.paymentType.card',
    defaultMessage: 'Card'
  },
  MIXED: {
    id: 'trip.drawer.notes.paymentType.mixed',
    defaultMessage: 'Mixed'
  }
})

const FilesWrapper = styled.div`
  display: grid;
  gap: 8px;
`

const TripNotesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
`

const NoteFormWrapper = styled.form`
  display: flex;
  flex-direction: column;
  gap: 29px;
`

const NoteFormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media ${device.tablet} {
    flex-direction: row;
    gap: 16px;
  }
`

const NoteFormColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
`

const ActionsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`

interface ITripNotesProps {
  tripId: string
  notes: TripV2Note[]
  driver?: TripV2Driver | null
}

export const TripNotes = ({ tripId, notes, driver }: ITripNotesProps): ReactElement => {
  const intl = useIntl()
  const client = useApolloClient()
  const pushFileToS3 = usePushFileToS3()
  const [s3Error, setS3Error] = useState<string | undefined>()

  const [addTripNote, { error }] = useMutation(ADD_TRIP_NOTE, {
    onCompleted: () => undefined
  })
  const [addTripDriverRSVPNote, { error: driverRSVPNoteError }] = useMutation(ADD_TRIP_DRIVER_RSVP_NOTE, {
    onCompleted: () => undefined
  })

  const [addTripDriverRealDistanceNote, { error: driverRealDistanceNoteError }] = useMutation(
    ADD_TRIP_DRIVER_REAL_DISTANCE_NOTE,
    {
      onCompleted: () => undefined
    }
  )

  let content = <></>
  if (isEmpty(notes)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="trips.drawer.notes.empty.description"
            defaultMessage="There were no notes found for this trip"
          />
        </EmptyState.Description>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        {[...notes].reverse().map((note, index) => {
          return <NoteItem key={index} note={note} />
        })}
      </>
    )
  }

  const onSubmit = async (
    values: INoteFormValues,
    { setSubmitting, resetForm }: FormikHelpers<INoteFormValues>
  ): Promise<void> => {
    const filesToUpload: string[] = []
    try {
      for (const file of values.files) {
        const filePath = await pushFileToS3(file)
        filesToUpload.push(filePath)
      }
    } catch (ex) {
      // @ts-expect-error FIXME
      setS3Error(ex?.message)
      setSubmitting(false)
      return
    }

    try {
      if (values.category === TripNoteCategoryInput.Odometer) {
        const input = mapNoteFormToRealDistanceInput(tripId || '', Number(values.realDistance), values, filesToUpload)
        await addTripDriverRealDistanceNote({
          variables: {
            input
          }
        })
      } else if (values.category === TripV2NoteCategory.DriverRsvp) {
        const input = mapNoteFormToRSVPNoteInput(tripId || '', driver?.id || '', values, filesToUpload)
        await addTripDriverRSVPNote({
          variables: {
            input
          }
        })
      } else {
        const input = mapNoteFormToInput(tripId ?? '', values)

        await addTripNote({
          variables: {
            input: {
              ...input,
              filePaths: filesToUpload
            }
          }
        })
      }
      const isDriverRsvpNote = values.category === TripV2NoteCategory.DriverRsvp
      client.writeFragment({
        id: client.cache.identify({
          __typename: 'TripV2',
          id: tripId
        }),
        fragment: TRIP_NOTE_FRAGMENT,
        data: {
          notes: [
            ...notes,
            {
              __typename: 'TripV2Note',
              category: values.category as TripV2NoteCategory,
              subcategory: null,
              message: values.message,
              changeType: isDriverRsvpNote ? TripV2NoteChangeType.AddDriverRsvpNote : TripV2NoteChangeType.AddNote,
              user: {
                id: '',
                firstName: '',
                lastName: '',
                username: ''
              },
              createdAt: new Date().toISOString(),
              cashFlow: values.paymentIncidents
                ? {
                    type: values.operationType as string,
                    amount: integerToCents(values.cashAmount) + integerToCents(values.cardAmount)
                  }
                : null,
              files: values.files.map((file) => ({
                name: file.name,
                path: URL.createObjectURL(file)
              })),
              distance: values.realDistance ? kilometersToMeters(Number(values.realDistance)) : null,
              driver: isDriverRsvpNote
                ? {
                    id: driver?.id || '',
                    firstName: driver?.firstName || '',
                    lastName: driver?.lastName || '',
                    rsvpStatus: values.driverRSVPStatus as TripNoteDriverRsvpStatus
                  }
                : null
            }
          ]
        }
      })

      setSubmitting(false)
      resetForm()
    } catch (ex) {
      setSubmitting(false)
    }
  }

  const initialValues: INoteFormValues = {
    category: '',
    paymentIncidents: false,
    operationType: '',
    paymentType: GTPaymentIncidencePaymentMethodInput.Cash,
    cashAmount: '',
    cardAmount: '',
    message: '',
    files: [],
    driverRSVPStatus: null,
    realDistance: ''
  }

  return (
    <TripNotesWrapper>
      <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={tripNoteSchema(intl)}>
        {({
          values,
          dirty,
          setFieldValue,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          errors
        }): ReactElement => {
          const isCashMethod = values.paymentType === GTPaymentIncidencePaymentMethodInput.Cash
          const isCardMethod = values.paymentType === GTPaymentIncidencePaymentMethodInput.Card
          const isMixedMethod = values.paymentType === 'MIXED'
          const showCashAmount = !isCardMethod
          const showCardAmount = !isCashMethod

          return (
            <NoteFormWrapper onSubmit={handleSubmit}>
              <Select
                id="category"
                value={values.category}
                onChange={handleChange}
                onBlur={handleBlur}
                label={intl.formatMessage(messages.categoryLabel)}
                touched={touched.category}
                errors={errors.category}
              >
                <option value="">{intl.formatMessage(messages.categoryPlaceholder)}</option>
                {NOTE_CATEGORIES.map((noteCategory) => {
                  const isDisabled = noteCategory === TripV2NoteCategory.DriverRsvp && !driver

                  return (
                    <option key={noteCategory} value={noteCategory} disabled={isDisabled}>
                      {safeFormatMessage({ intl, messages: categoryOptions, key: noteCategory })}
                    </option>
                  )
                })}
              </Select>
              {values.category === TripNoteCategoryInput.Odometer && (
                <DistanceContainer>
                  <label>
                    <Label>
                      <FormattedMessage id="trip.drawer.notes.realDistance.label" defaultMessage="Real distance" />
                    </Label>
                    <InputAddon.Wrapper touched={touched.realDistance} errors={errors.realDistance} isFocused={false}>
                      <StyledNumberInput
                        value={values.realDistance}
                        onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                          setFieldValue('realDistance', e.target.value)
                        }}
                        onBlur={handleBlur}
                        error={errors.realDistance}
                        placeholder="0"
                        step="0.001"
                        type="number"
                      />
                      <InputAddon.Addon>
                        <FormattedMessage id="dictionary.km" defaultMessage="Km" />
                      </InputAddon.Addon>
                    </InputAddon.Wrapper>
                    {touched.realDistance && errors.realDistance ? (
                      <ErrorMessage>{errors?.realDistance}</ErrorMessage>
                    ) : null}
                  </label>
                </DistanceContainer>
              )}
              {values.category === TripV2NoteCategory.DriverRsvp ? (
                <DriverRSVPNote driver={driver as TripV2Driver} />
              ) : (
                <>
                  <SwitchWithLabel
                    label={intl.formatMessage(messages.paymentIncidentsLabel)}
                    value={values.paymentIncidents}
                    onChange={(value): void => {
                      setFieldValue('paymentIncidents', value)
                    }}
                  />
                  {values.paymentIncidents && (
                    <>
                      <NoteFormRow>
                        <NoteFormColumn>
                          <Select
                            id="operationType"
                            value={values.operationType}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            label={intl.formatMessage(messages.operationTypeLabel)}
                            touched={touched.operationType}
                            errors={errors.operationType}
                          >
                            <option value="">{intl.formatMessage(messages.operationTypePlaceholder)}</option>
                            {Object.entries(operationTypeOptions).map(([key, value]) => (
                              <option key={key} value={key}>
                                {intl.formatMessage(value)}
                              </option>
                            ))}
                          </Select>
                        </NoteFormColumn>
                        <NoteFormColumn>
                          <Select
                            id="paymentType"
                            value={values.paymentType}
                            onChange={({ target: { value } }: ChangeEvent<HTMLSelectElement>): void => {
                              setFieldValue('paymentType', value)
                              setFieldValue('cashAmount', '')
                              setFieldValue('cardAmount', '')
                            }}
                            onBlur={handleBlur}
                            label={intl.formatMessage(messages.paymentTypeLabel)}
                            touched={touched.paymentType}
                            errors={errors.paymentType}
                          >
                            {Object.entries(paymentTypeOptions).map(([key, value]) => (
                              <option key={key} value={key}>
                                {intl.formatMessage(value)}
                              </option>
                            ))}
                          </Select>
                        </NoteFormColumn>
                      </NoteFormRow>
                      <NoteFormRow>
                        {showCashAmount && (
                          <NoteFormColumn>
                            {isMixedMethod && (
                              <Heading.Label>
                                <FormattedMessage
                                  id="trip.drawer.notes.cashAmount.label"
                                  defaultMessage="Cash Amount"
                                />
                              </Heading.Label>
                            )}
                            <InputCurrency
                              id="cashAmount"
                              value={sanitizePrice(Number(values.cashAmount))}
                              placeholder="$0.00"
                              onChange={(value): void => {
                                setFieldValue('cashAmount', value)
                              }}
                              onBlur={handleBlur}
                              hideSuffix
                              touched={touched.cashAmount}
                              errors={errors.cashAmount}
                            />
                          </NoteFormColumn>
                        )}
                        {showCardAmount && (
                          <NoteFormColumn>
                            {isMixedMethod && (
                              <Heading.Label>
                                <FormattedMessage
                                  id="trip.drawer.notes.cardAmount.label"
                                  defaultMessage="Card Amount"
                                />
                              </Heading.Label>
                            )}
                            <InputCurrency
                              id="cardAmount"
                              value={sanitizePrice(Number(values.cardAmount))}
                              placeholder="$0.00"
                              onChange={(value): void => {
                                setFieldValue('cardAmount', value)
                              }}
                              onBlur={handleBlur}
                              hideSuffix
                              touched={touched.cardAmount}
                              errors={errors.cardAmount}
                            />
                          </NoteFormColumn>
                        )}
                      </NoteFormRow>
                    </>
                  )}
                </>
              )}
              <Textarea
                id="message"
                value={values.message}
                label={intl.formatMessage(messages.messageLabel)}
                placeholder={intl.formatMessage(messages.messagePlaceholder)}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched.message}
                errors={errors.message}
              />
              {!isEmpty(values.files) && (
                <FilesWrapper>
                  {values.files.map((file, index) => (
                    <FilePreview
                      key={index}
                      file={file}
                      onRemoveFile={(): void => {
                        setFieldValue(
                          'files',
                          values.files.filter((_, i) => i !== index)
                        )
                      }}
                    />
                  ))}
                </FilesWrapper>
              )}
              {s3Error && (
                <ErrorMessage>
                  <FormattedMessage
                    id="trip.new.note.error"
                    defaultMessage="There was an error while uploading the files"
                  />
                </ErrorMessage>
              )}
              {error && (
                <ErrorMessage errors={getUnprocessableErrors(error)}>
                  <FormattedMessage
                    id="trip.new.note.error"
                    defaultMessage="There was an error while adding the note"
                  />
                </ErrorMessage>
              )}
              {driverRSVPNoteError && (
                <ErrorMessage errors={getUnprocessableErrors(driverRSVPNoteError)}>
                  <FormattedMessage
                    id="trip.new.note.error"
                    defaultMessage="There was an error while confirming the driver RSVP status"
                  />
                </ErrorMessage>
              )}
              {driverRealDistanceNoteError && (
                <ErrorMessage errors={getUnprocessableErrors(driverRealDistanceNoteError)}>
                  <FormattedMessage
                    id="trip.new.note.error"
                    defaultMessage="There was an error while adding the real distance note"
                  />
                </ErrorMessage>
              )}
              <ActionsWrapper>
                <InputFile
                  input={<IconButton icon={<ClipIcon />} />}
                  onChange={function (file: '' | File): void {
                    if (file instanceof File) {
                      setFieldValue(`files`, [...values.files, file])
                    }
                  }}
                />
                <Button type="submit" kind="primary" disabled={!dirty} loading={isSubmitting}>
                  <FormattedMessage id="dictionary.send" defaultMessage="Send" />
                </Button>
              </ActionsWrapper>
            </NoteFormWrapper>
          )
        }}
      </Formik>
      {content}
    </TripNotesWrapper>
  )
}
